import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { useSelector } from 'react-redux';
import { HbrCard, HbrIcon, HbrSpinner, HbrTag } from '@harbor/elements-react-standalone';
import { checkCircle, arrowSquareOut, info } from '@harbor/elements/icons';
import { RootState } from '../../../support/store';
import { fetchItems } from '../../../support/queries';
import {
  useEnsureCourseEnrollment,
  useMoodleCourseContent,
  useMoodleCourses,
} from '../../../hooks/useMoodleHooks';
import { hasToolboxDbRecord } from '../../../support/journeyMap/contentSources';
import StbButton from '../../security-toolbox-ui/StbButton';
import { computeRequiredProgressFromSections } from '../../Common/Moodle/Learning/MoodleLearningProgress';

type Props = {
  item: any;
  alreadyCompleted?: boolean;
  onComplete?: () => Promise<void> | void;
  onRefreshCompletion?: () => Promise<boolean>;
  returnTo?: string;
  instance?: 'external' | 'internal';
  onLaunchLearning?: () => void;
  completionReason?: string;
};

const getResourcePath = (item: any): string | null => {
  if (!item?.content_ref_type || !item?.content_ref_id) return null;

  // A `moodle_course` reference carries a Moodle course id, so there is no toolbox_db row to
  // fetch — and `db/moodle-course/{id}/` is not a model at all. The course id is read straight
  // off the reference further down instead.
  if (!hasToolboxDbRecord(item.content_ref_type)) return null;

  return `db/${String(item.content_ref_type).replace(/_/g, '-')}/${item.content_ref_id}/`;
};

const stripHtml = (value?: string) => {
  if (!value) return '';
  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim();
};

function resolveInstance(item: any, explicitInstance?: 'external' | 'internal'): 'external' | 'internal' {
  if (explicitInstance) return explicitInstance;

  const provider = String(item?.provider || '').toLowerCase();
  const providerConfigInstance = String(item?.provider_config?.instance || '').toLowerCase();
  const providerMode = String(item?.provider_config?.mode || '').toLowerCase();
  const metadataInstance = String(item?.metadata?.instance || '').toLowerCase();

  if (
    provider.includes('cyber') ||
    provider.includes('internal') ||
    providerConfigInstance === 'internal' ||
    providerMode === 'internal' ||
    metadataInstance === 'internal'
  ) {
    return 'internal';
  }

  return 'external';
}

function getLearningCompletionStorageKey(instance: 'external' | 'internal', courseId: number | string) {
  return `learning:localCompletion:moodle:${instance}:${courseId}`;
}

const JourneyMapMoodleCourseModalContent: React.FC<Props> = ({
                                                               item,
                                                               alreadyCompleted = false,
                                                               onComplete,
                                                               onRefreshCompletion,
                                                               returnTo,
                                                               instance: explicitInstance,
                                                               onLaunchLearning,
                                                               completionReason,
                                                             }) => {
  const navigate = useNavigate();

  const instance = useMemo(() => resolveInstance(item, explicitInstance), [item, explicitInstance]);

  const [showCompletionPage, setShowCompletionPage] = useState(alreadyCompleted);
  const [locallyCompletedIds, setLocallyCompletedIds] = useState<Set<string>>(new Set());
  const [isLaunching, setIsLaunching] = useState(false);

  useEffect(() => {
    setShowCompletionPage(alreadyCompleted);
  }, [item?.id, alreadyCompleted]);

  const resourcePath = getResourcePath(item);

  const referencedQuery = useQuery({
    queryKey: ['journey-map-item-content-ref', item?.id, resourcePath],
    queryFn: async () => {
      if (!resourcePath) return null;
      return await fetchItems(resourcePath, {}, { includePageSize: false });
    },
    ...{
        enabled: !!resourcePath,
        staleTime: 60_000,
      },
  });

  const fallbackByMoodleIdQuery = useQuery({
    queryKey: ['journey-map-ai-snackable-fallback-by-moodle-id', item?.id, item?.content_ref_id],
    queryFn: async () => {
      const rawId = item?.content_ref_id;
      if (rawId == null || String(rawId).trim() === '') return null;
      const moodleId = Number(rawId);
      if (!Number.isFinite(moodleId)) return null;

      if (
        referencedQuery.data &&
        (referencedQuery.data.deployed_moodle_id != null || referencedQuery.data.staged_moodle_id != null)
      ) {
        return null;
      }

      const rows = await fetchItems('db/ai-snackable-course/', {}, { includePageSize: false }).catch(() => []);
      const list = Array.isArray(rows) ? rows : [];

      const matched =
        list.find((r: any) => Number(r?.deployed_moodle_id) === moodleId) ||
        list.find((r: any) => Number(r?.staged_moodle_id) === moodleId) ||
        null;

      return matched;
    },
    ...{
        // A `moodle_course` reference exists precisely because no snackable row covers the
        // course, so scanning the whole snackable table for one is guaranteed to come back
        // empty. The course id is already on the reference.
        enabled: !!item?.content_ref_id && item?.content_ref_type !== 'moodle_course',
        staleTime: 60_000,
      },
  });

  const referencedRecord = useMemo(() => {
    return referencedQuery.data || fallbackByMoodleIdQuery.data || null;
  }, [referencedQuery.data, fallbackByMoodleIdQuery.data]);

  const deployedMoodleId = useMemo(() => {
    if (
      referencedRecord?.deployed_moodle_id != null &&
      String(referencedRecord.deployed_moodle_id).trim() !== ''
    ) {
      return Number(referencedRecord.deployed_moodle_id);
    }

    if (
      referencedRecord?.staged_moodle_id != null &&
      String(referencedRecord.staged_moodle_id).trim() !== ''
    ) {
      return Number(referencedRecord.staged_moodle_id);
    }

    const configCourseId = item?.provider_config?.moodle?.course_id;
    if (configCourseId != null && String(configCourseId).trim() !== '') {
      return Number(configCourseId);
    }

    if (item?.content_ref_id != null && String(item.content_ref_id).trim() !== '') {
      const maybeMoodleId = Number(item.content_ref_id);
      if (Number.isFinite(maybeMoodleId)) return maybeMoodleId;
    }

    return null;
  }, [referencedRecord, item]);

  const { data: sections, isLoading, error } = useMoodleCourseContent(
    deployedMoodleId || undefined,
    { enabled: !!deployedMoodleId },
    instance
  );

  const { data: allCourses } = useMoodleCourses(undefined, instance);

  // Eagerly provision + enrol the learner into an internal Cyber Academy course the
  // moment its modal resolves, so completion tracks from their first visit rather
  // than depending on a prior manual enrolment. Best-effort and fired once per
  // (course, learner); the completion read self-heals server-side regardless.
  const userEmail = useSelector((s: RootState) => s.user?.userData?.email as string | undefined);
  const ensureEnrollment = useEnsureCourseEnrollment();
  const provisionedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (instance !== 'internal' || !deployedMoodleId || !userEmail) return;
    const key = `${deployedMoodleId}:${userEmail}`;
    if (provisionedKeyRef.current === key) return;
    provisionedKeyRef.current = key;
    ensureEnrollment.mutate({ email: userEmail, courseId: deployedMoodleId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, deployedMoodleId, userEmail]);

  const courseFromList =
    allCourses?.find((c: any) => String(c.id) === String(deployedMoodleId)) ?? null;

  const courseName =
    referencedRecord?.title ||
    referencedRecord?.fullname ||
    referencedRecord?.name ||
    courseFromList?.fullname ||
    courseFromList?.shortname ||
    item?.name ||
    'Course';

  const courseSummary =
    referencedRecord?.summary ||
    referencedRecord?.description ||
    courseFromList?.summary ||
    item?.description ||
    '';

  useEffect(() => {
    if (!deployedMoodleId) {
      setLocallyCompletedIds(new Set());
      return;
    }

    const readLocalCompletion = () => {
      const raw = localStorage.getItem(getLearningCompletionStorageKey(instance, deployedMoodleId));
      if (!raw) {
        setLocallyCompletedIds(new Set());
        return;
      }

      try {
        const parsed = JSON.parse(raw);
        const ids = Array.isArray(parsed?.itemIds) ? parsed.itemIds.map(String) : [];
        setLocallyCompletedIds(new Set(ids));
      } catch {
        setLocallyCompletedIds(new Set());
      }
    };

    readLocalCompletion();

    const handleFocus = () => readLocalCompletion();
    const handleCompletionUpdated = () => readLocalCompletion();

    window.addEventListener('focus', handleFocus);
    window.addEventListener('learning-completion-updated', handleCompletionUpdated);
    window.addEventListener('moodle:activityCompleted', handleCompletionUpdated);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('learning-completion-updated', handleCompletionUpdated);
      window.removeEventListener('moodle:activityCompleted', handleCompletionUpdated);
    };
  }, [instance, deployedMoodleId]);

  const safeSections = useMemo(() => (Array.isArray(sections) ? sections : []), [sections]);

  const safeSectionsWithLocalCompletion = useMemo(() => {
    return safeSections.map((section: any) => ({
      ...section,
      modules: (section?.modules || []).map((mod: any) => {
        const backendComplete = !!mod?.completiondata?.isoverallcomplete;
        const localComplete = locallyCompletedIds.has(String(mod?.id));

        return {
          ...mod,
          completiondata: {
            ...(mod?.completiondata || {}),
            isoverallcomplete: backendComplete || localComplete,
          },
        };
      }),
    }));
  }, [safeSections, locallyCompletedIds]);

  const requiredProgress = useMemo(() => {
    return computeRequiredProgressFromSections(safeSectionsWithLocalCompletion, locallyCompletedIds);
  }, [safeSectionsWithLocalCompletion, locallyCompletedIds]);

  const completedRequiredModuleCount = requiredProgress.completed;
  const totalRequiredModuleCount = requiredProgress.total;
  const localCourseProgress = requiredProgress.percent;

  const totalSections = safeSectionsWithLocalCompletion.length;

  const learningHref = deployedMoodleId
    ? `/learning/moodle/${instance}/course/${deployedMoodleId}?fullscreen=1${
      returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''
    }`
    : null;

  const handleOpenLearning = () => {
    if (!learningHref || isLaunching) return;

    setIsLaunching(true);

    onLaunchLearning?.();
    navigate(learningHref);

    if (onRefreshCompletion) {
      void (async () => {
        try {
          const done = await onRefreshCompletion();
          if (done) {
            await onComplete?.();
            setShowCompletionPage(true);
          }
        } catch {
          // ignore background refresh failures during launch
        } finally {
          setIsLaunching(false);
        }
      })();

      return;
    }

    setIsLaunching(false);
  };

  const resolvedCompletionReason =
    completionReason ||
    (localCourseProgress >= 100
      ? 'Completed by meeting course progress requirements.'
      : showCompletionPage
        ? 'Completion has been satisfied for this journey item.'
        : '');

  if (referencedQuery.isLoading || fallbackByMoodleIdQuery.isLoading || isLoading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
        <HbrSpinner size="large">
          <div slot="label">Loading course...</div>
        </HbrSpinner>
      </div>
    );
  }

  if (error || referencedQuery.error) {
    return (
      <HbrCard
        style={
          {
            '--box-shadow': 'none',
            '--border-width': '2px',
          } as React.CSSProperties
        }
      >
        <div className="hbr-css__layout-col-sm">
          <div className="hbr-css__text-primary-sm-semibold">Unable to load course.</div>
          <div className="hbr-css__text-secondary-sm">
            The Moodle/Cyber Academy course content could not be loaded.
          </div>
        </div>
      </HbrCard>
    );
  }

  if (!deployedMoodleId) {
    return (
      <HbrCard
        style={
          {
            '--box-shadow': 'none',
            '--border-width': '2px',
          } as React.CSSProperties
        }
      >
        <div className="hbr-css__layout-col-sm">
          <div className="hbr-css__text-primary-sm-semibold">No deployed Moodle course found.</div>
          <div className="hbr-css__text-secondary-sm">
            This journey item is linked to content, but no deployed Moodle course id is available.
          </div>
        </div>
      </HbrCard>
    );
  }

  return (
    <div className="hbr-css__layout-col-md" style={{ minHeight: 0 }}>
      <HbrCard
        style={
          {
            '--box-shadow': 'none',
            '--border-width': '2px',
            '--border-color': 'var(--hbr-color-neutral-border-weak)',
          } as React.CSSProperties
        }
      >
        <div className="hbr-css__layout-col-md">
          <div className="hbr-css__layout-row-sm hbr-css__layout-justify-between hbr-css__layout-align-center">
            <div className="hbr-css__layout-col-3xs">
              <div className="hbr-css__text-heading-md-bold">{courseName}</div>
              <div className="hbr-css__text-secondary-sm">
                {completedRequiredModuleCount} / {totalRequiredModuleCount} required activities complete
              </div>
            </div>

            <div className="hbr-css__layout-row-xs hbr-css__layout-wrap">
              <HbrTag size="small">{totalSections} sections</HbrTag>
              <HbrTag size="small">{localCourseProgress}% complete</HbrTag>
              <HbrTag size="small">{instance === 'internal' ? 'Cyber Academy' : 'Moodle'}</HbrTag>
            </div>
          </div>

          {stripHtml(courseSummary) ? (
            <div className="hbr-css__text-secondary-sm">{stripHtml(courseSummary)}</div>
          ) : null}

          <div
            style={{
              width: '100%',
              height: '10px',
              borderRadius: '999px',
              backgroundColor: 'var(--hbr-color-neutral-bg-weak)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${localCourseProgress}%`,
                height: '100%',
                backgroundColor: 'var(--hbr-color-info)',
                borderRadius: '999px',
                transition: 'width 250ms ease',
              }}
            />
          </div>

          {showCompletionPage ? (
            <HbrCard
              sentiment="success"
              style={
                {
                  '--border-width': '2px',
                } as React.CSSProperties
              }
            >
              <div className="hbr-css__layout-col-sm">
                <div className="hbr-css__layout-row-sm hbr-css__layout-align-center">
                  <HbrIcon asset={checkCircle} style={{ fontSize: '24px' }} />
                  <div className="hbr-css__text-heading-md-bold">Course Complete</div>
                </div>

                <div className="hbr-css__text-secondary-sm">{resolvedCompletionReason}</div>
              </div>
            </HbrCard>
          ) : (
            <HbrCard
              style={
                {
                  '--box-shadow': 'none',
                  '--border-width': '2px',
                } as React.CSSProperties
              }
            >
              <div className="hbr-css__layout-row-3xs hbr-css__layout-align-center hbr-css__text-secondary-sm">
                <HbrIcon asset={info} />
                Launch the course to continue progress and complete tracked activities.
              </div>
            </HbrCard>
          )}

          <div>
            <StbButton
              role="save"
              icon={arrowSquareOut}
              onClick={handleOpenLearning}
              disabled={isLaunching}
            >
              {isLaunching
                ? 'Opening Course...'
                : showCompletionPage
                  ? 'Review Course'
                  : alreadyCompleted
                    ? 'Resume Training'
                    : 'Start Training'}
            </StbButton>
          </div>
        </div>
      </HbrCard>
    </div>
  );
};

export default JourneyMapMoodleCourseModalContent;